import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Zap, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConnectShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (refresh = false) => {
    if (refresh) setBusy(true);
    try { const { data: d } = await axios.get(`${API}/public/wa-qr/${token}`, { params: refresh ? { refresh: 1 } : {} }); setData(d); setError(""); }
    catch (e) { setError(e.response?.data?.detail || "Não foi possível carregar o QR Code."); if (e.response?.status === 404 || e.response?.status === 410) setData(null); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line
  useEffect(() => {
    if (!data || data.status === "conectado") return;
    const t = setInterval(() => load(), 5000);
    return () => clearInterval(t);
  }, [data?.status, token]); // eslint-disable-line

  const expired = error && !data;
  return (
    <div className="relative min-h-screen overflow-hidden bg-background px-5 py-10">
      <div className="pointer-events-none absolute inset-0"><div className="absolute -left-32 top-0 h-96 w-96 rounded-full bg-cyan-500/15 blur-[130px]" /></div>
      <div className="relative z-10 mx-auto max-w-md" data-testid="connect-share-page">
        <div className="mb-6 flex items-center gap-2">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-cyan-400 text-slate-950"><Zap className="h-4 w-4" strokeWidth={2.6} /></div>
          <span className="font-display text-lg font-extrabold text-white">Flow<span className="text-cyan-400">IA</span></span>
        </div>
        <h1 className="font-display text-3xl font-extrabold text-white sm:text-4xl">Conectar WhatsApp</h1>
        {data?.company && <p className="mt-1 text-sm text-slate-400" data-testid="connect-share-company">{data.company}</p>}

        <div className="glow-card mt-6 rounded-2xl p-6">
          {expired ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="connect-share-expired">
              <AlertTriangle className="h-10 w-10 text-amber-300" />
              <p className="text-slate-200">{error}</p>
            </div>
          ) : !data ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-8 w-8 animate-spin text-cyan-400" /></div>
          ) : data.status === "conectado" ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="connect-share-done">
              <CheckCircle2 className="h-12 w-12 text-emerald-400" />
              <p className="font-display text-xl font-bold text-white">WhatsApp conectado!</p>
              <p className="text-sm text-slate-400">Pode fechar esta página. A IA já está pronta para atender no painel da FlowIA.</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              {data.qr_base64 ? (
                <div className="grid h-64 w-64 place-items-center rounded-xl border border-cyan-400/30 bg-white p-2"><img src={data.qr_base64} alt="QR Code WhatsApp" className="h-full w-full" data-testid="connect-share-qr" /></div>
              ) : (
                <div className="grid h-64 w-64 place-items-center rounded-xl border border-slate-800 bg-slate-900/60"><Loader2 className="h-6 w-6 animate-spin text-cyan-400" /></div>
              )}
              <p className="flex items-center gap-1.5 text-xs text-cyan-300"><Loader2 className="h-3 w-3 animate-spin" /> Aguardando leitura — esta página atualiza sozinha.</p>
              <ol className="w-full space-y-1.5 text-sm text-slate-300">
                <li><Smartphone className="mr-1.5 inline h-4 w-4 text-cyan-300" />No celular da empresa, abra o WhatsApp › <b>Aparelhos conectados</b>.</li>
                <li className="pl-6">Toque em <b>Conectar um aparelho</b> e aponte a câmera para este QR Code.</li>
              </ol>
              <Button variant="outline" size="sm" onClick={() => load(true)} disabled={busy} className="border-slate-700 bg-slate-900/50 text-slate-300" data-testid="connect-share-refresh"><RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Gerar novo QR</Button>
              {error && <p className="flex items-center gap-1.5 text-xs text-red-300" data-testid="connect-share-error"><AlertTriangle className="h-3.5 w-3.5" /> {error}</p>}
            </div>
          )}
        </div>
        <p className="mt-4 text-center text-xs text-slate-500">Link temporário e seguro, válido por poucos minutos. Nenhum dado da sua conta é exibido aqui.</p>
      </div>
    </div>
  );
}
